import React from 'react';
import { motion } from 'framer-motion';
import { Building, Phone, Mail, MapPin, FileText, Shield, Link as LinkIcon, Copyright, Lock } from 'lucide-react';

const Impressum = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="impressum-page">
      {/* Hero Section */}
      <section className="bg-[#003B7A] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Building className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
              Impressum
            </h1>
            <p className="text-gray-300 text-lg">Rechtliche Informationen</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Company Info Card */}
            <div className="bg-gray-50 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#003B7A] mb-6 flex items-center gap-3">
                <Building className="w-6 h-6 text-[#FFD700]" />
                Marali Immobilien AG
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#003B7A] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Adresse</p>
                    <p className="text-gray-600">Schellenberg 5</p>
                    <p className="text-gray-600">7408 Cazis</p>
                    <p className="text-gray-600">Schweiz</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#003B7A] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Rechtsform & Sitz</p>
                    <p className="text-gray-600">Aktiengesellschaft</p>
                    <p className="text-gray-600">Sitz: Cazis</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#003B7A] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Telefon</p>
                    <a href="tel:+41792649063" className="text-[#003B7A] hover:text-[#FFD700] transition-colors">
                      079 264 90 63
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#003B7A] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">E-Mail</p>
                    <a href="mailto:furis@maraliimmobilien.ch" className="text-[#003B7A] hover:text-[#FFD700] transition-colors">
                      furis@maraliimmobilien.ch
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration Info */}
            <div className="bg-[#003B7A] text-white rounded-2xl p-8 mb-12">
              <h2 className="text-xl font-bold mb-6">Handelsregister & Identifikation</h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-300">UID</p>
                  <p className="font-semibold">CHE-416.967.022</p>
                </div>
                <div>
                  <p className="text-gray-300">CH-ID</p>
                  <p className="font-semibold">CH-350-3014739-6</p>
                </div>
                <div>
                  <p className="text-gray-300">Handelsregisteramt</p>
                  <p className="font-semibold">Kanton Graubünden</p>
                </div>
                <div>
                  <p className="text-gray-300">Mehrwertsteuernummer</p>
                  <p className="font-semibold">CHE-416.967.022 MWST</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-gray-300">Vertretungsberechtigte Person</p>
                  <p className="font-semibold">Luchian-Gabriel Furis</p>
                </div>
              </div>
            </div>

            {/* Legal Sections */}
            <div className="space-y-10">
              <div>
                <h2 className="text-2xl font-bold text-[#003B7A] mb-4 flex items-center gap-3">
                  <Shield className="w-6 h-6 text-[#FFD700]" />
                  Haftungsausschluss
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Marali Immobilien AG übernimmt keine Gewähr für die Richtigkeit, Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen auf dieser Website. Haftungsansprüche gegen die Marali Immobilien AG wegen Schäden materieller oder immaterieller Art, welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten Informationen, durch Missbrauch der Verbindung oder durch technische Störungen entstanden sind, werden ausgeschlossen.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Alle Angebote sind unverbindlich. Die Marali Immobilien AG behält es sich ausdrücklich vor, Teile der Seiten oder das gesamte Angebot ohne gesonderte Ankündigung zu verändern, zu ergänzen, zu löschen oder die Veröffentlichung zeitweise oder endgültig einzustellen.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#003B7A] mb-4 flex items-center gap-3">
                  <LinkIcon className="w-6 h-6 text-[#FFD700]" />
                  Haftung für Links
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Verweise und Links auf Websites Dritter liegen ausserhalb unseres Verantwortungsbereichs. Es wird jegliche Verantwortung für solche Websites abgelehnt. Der Zugriff und die Nutzung solcher Websites erfolgen auf eigene Gefahr des Nutzers oder der Nutzerin.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#003B7A] mb-4 flex items-center gap-3">
                  <Copyright className="w-6 h-6 text-[#FFD700]" />
                  Urheberrechte
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien auf der Website gehören ausschliesslich der Marali Immobilien AG oder den speziell genannten Rechtsinhabern. Für die Reproduktion jeglicher Elemente ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#003B7A] mb-4 flex items-center gap-3">
                  <Lock className="w-6 h-6 text-[#FFD700]" />
                  Datenschutz
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Basierend auf Artikel 13 der schweizerischen Bundesverfassung und den datenschutzrechtlichen Bestimmungen des Bundes (Datenschutzgesetz, DSG) hat jede Person Anspruch auf Schutz ihrer Privatsphäre sowie auf Schutz vor Missbrauch ihrer persönlichen Daten. Wir halten diese Bestimmungen ein. Persönliche Daten werden streng vertraulich behandelt und weder an Dritte verkauft noch weitergegeben.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In enger Zusammenarbeit mit unseren Hosting-Providern bemühen wir uns, die Datenbanken so gut wie möglich vor fremden Zugriffen, Verlusten, Missbrauch oder Fälschung zu schützen.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Beim Zugriff auf unsere Websites werden folgende Daten in Logfiles gespeichert: IP-Adresse, Datum, Uhrzeit, Browser-Anfrage und allgemein übertragene Informationen zum Betriebssystem resp. Browser. Diese Nutzungsdaten bilden die Basis für statistische, anonyme Auswertungen, so dass Trends erkennbar sind, anhand derer wir unsere Angebote entsprechend verbessern können.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Gemäss Bundesgesetz betreffend die Überwachung des Post- und Fernmeldeverkehrs (BÜPF) besteht eine gesetzliche Pflicht zur Aufbewahrung von Verbindungsdaten während mindestens sechs Monaten.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Impressum;
